'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Calculator, Loader2, MapPin, Ruler } from 'lucide-react';
import { useEstimateIntro } from '../hooks/use-estimate-intro';
import { useConceptCurrentEstimate } from '../hooks/use-design-line';
import { EstimateEditor } from './EstimateEditor';
import { EstimateDraftSettings } from './EstimateDraftSettings';
import {
  NestedSheetStackProvider,
  NoticeBanner,
  PromoBenefits,
  type PromoChip,
} from '@/shared/ui';
import { Button } from '@/shared/ui/shadcn/button';
import { cn } from '@/shared/lib/cn';

interface EstimateTabContentProps {
  projectId: string;
  conceptId: string;
  /** Stacked (single-column) layout: page-flow spacing instead of the fixed
   *  inspector column. Mirrors the album/render panel `isStacked` switch. */
  isStacked?: boolean;
  /** Two-column layout: the settings list + primary CTA live in the inspector
   *  aside (EstimateSettingsPanel) — the content column must not render its
   *  inline copies (applies to the editor AND the working intro). */
  settingsHostedExternally?: boolean;
}

/**
 * Estimate tab.
 *
 * Two states:
 * - No estimate yet → WORKING intro: hero + the same settings group the
 *   completed estimate shows, backed by an in-memory draft
 *   (EstimateDraftSettings); Generate POSTs the draft. On the two-column
 *   layout the settings + Generate live in the inspector aside
 *   (EstimateSettingsPanel) — only the hero stays here. No client-side plan
 *   gate post-pivot — the server returns the estimate paywall on Generate
 *   (PackRequired) for non-pack users.
 * - An estimate exists → the editor (FE2): header + Materials/Labor sections,
 *   inline-editable line items, regenerate. The editor shows the current
 *   version of the design's line (docs/estimate/UI-SPEC-design-lines.md).
 *
 * All copy (intro) and business messages (failure reason, paywall) come from
 * the API; the client only owns static chrome labels.
 */
export function EstimateTabContent({
  projectId,
  conceptId,
  isStacked = false,
  settingsHostedExternally = false,
}: EstimateTabContentProps) {
  const t = useTranslations('common.estimate');
  const locale = useLocale();

  const { data: intro, isLoading: isLoadingIntro, isError, refetch } =
    useEstimateIntro(projectId, conceptId);

  // Region + measurement context chips for the promo ad (Apple "Brazil /
  // Metric" parity). Region label is server-driven; the units label is static
  // chrome keyed off the server `measurement_system` flag.
  const introChips: PromoChip[] = intro
    ? [
        ...(intro.region_label
          ? [{ icon: <MapPin />, label: intro.region_label }]
          : []),
        {
          icon: <Ruler />,
          label: t(`measurement.${intro.measurement_system}`),
        },
      ]
    : [];

  // The design's running estimate (ADR-001): the current version of its
  // design line, not the newest estimate calculated for this design.
  const {
    context: lineContext,
    estimate: currentEstimate,
    isLoading: isLoadingEstimates,
  } = useConceptCurrentEstimate(projectId, conceptId);

  // An estimate exists → render the editor. The NestedSheetStackProvider +
  // `relative` container host the project-goals sheet opened from the
  // facade-area link: two-column slides it over the estimate column, stacked
  // takes the full-viewport overlay (same switch as the render/album panels).
  if (currentEstimate && lineContext?.line) {
    return (
      <NestedSheetStackProvider isStacked={isStacked}>
        <div className={cn('relative flex flex-col', !isStacked && 'h-full')}>
          <EstimateEditor
            conceptId={conceptId}
            projectId={projectId}
            estimateId={currentEstimate.id}
            lineContext={lineContext}
            locale={locale}
            settingsHostedExternally={settingsHostedExternally}
          />
        </div>
      </NestedSheetStackProvider>
    );
  }

  const isLoading = isLoadingIntro || isLoadingEstimates;

  // No estimate yet → the intro (settings/draft). Cap it at a readable column
  // width and center it on BOTH layouts: an edge-to-edge intro (full-width
  // bullets + a metre-long Generate bar) reads as a stretched placeholder. This
  // is the SETTINGS surface, so it stays narrow — unlike the generated estimate
  // document (the editor branch above), which renders full width.
  return (
    <div
      className={cn(
        'flex flex-col gap-4 mx-auto w-full max-w-[560px]',
        !isStacked && 'h-full'
      )}
    >
      {isLoading && (
        <div className="flex flex-1 items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {isError && (
        <NoticeBanner
          variant="error"
          title={t('loadError')}
          action={
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              {t('retry')}
            </Button>
          }
        />
      )}

      {/* No estimate yet → WORKING intro: hero + draft settings + Generate (the
          settings/CTA move to the inspector aside on two-column). The provider
          + `relative` container host the drill-down NestedSheets (goals /
          currency / language / requirements) — editor-branch parity. No
          client-side plan gate: Generate POSTs the draft and the server returns
          the estimate paywall (PackRequired) for non-pack users. */}
      {!isLoading && intro && (
        <NestedSheetStackProvider isStacked={isStacked}>
          <div
            className={cn(
              'relative flex flex-col gap-4',
              !isStacked && 'flex-1'
            )}
          >
            {/* Promo ad — icon + server-driven heading + region/units context
                chips + benefit bullets (Apple/Android intro parity). The CTA
                lives with the settings, NOT here: on two-column the settings +
                Generate are hosted in the inspector aside (settingsHostedExternally),
                on stacked they follow below. */}
            <PromoBenefits
              icon={<Calculator />}
              title={intro.title}
              subtitle={intro.subtitle}
              chips={introChips}
              bullets={intro.bullets}
            />

            {!settingsHostedExternally && (
              <EstimateDraftSettings
                projectId={projectId}
                conceptId={conceptId}
                intro={intro}
                variant="inline"
              />
            )}
          </div>
        </NestedSheetStackProvider>
      )}
    </div>
  );
}
